import { Popover as BasePopover } from '@base-ui/react'
import clsx from 'clsx'
import type { ComponentProps, ReactNode } from 'react'

export interface PopoverRootProps {
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export interface PopoverTriggerProps {
  children?: ReactNode
  className?: string
  render?: BasePopover.Trigger.Props['render']
}

export interface PopoverContentProps {
  children: ReactNode
  className?: string
  side?: ComponentProps<typeof BasePopover.Positioner>['side']
  align?: ComponentProps<typeof BasePopover.Positioner>['align']
  collisionAvoidance?: ComponentProps<typeof BasePopover.Positioner>['collisionAvoidance']
  style?: ComponentProps<typeof BasePopover.Popup>['style']
}

function PopoverRoot({ children, ...props }: PopoverRootProps) {
  return <BasePopover.Root {...props}>{children}</BasePopover.Root>
}

function PopoverTrigger({ children, className, ...props }: PopoverTriggerProps) {
  return (
    <BasePopover.Trigger className={className} {...props}>
      {children}
    </BasePopover.Trigger>
  )
}

function PopoverContent({
  children,
  className,
  side = 'bottom',
  align = 'start',
  collisionAvoidance,
  ...props
}: PopoverContentProps) {
  return (
    <BasePopover.Portal>
      <BasePopover.Positioner
        side={side}
        align={align}
        sideOffset={8}
        collisionAvoidance={collisionAvoidance}
        className="z-50"
      >
        <BasePopover.Popup
          className={clsx(
            // Fixed design system styles (Rails dropdown match)
            'z-50 bg-white rounded-lg shadow border border-gray-200',
            // Default padding (can be overridden)
            'p-4',
            // User additions/overrides
            className
          )}
          {...props}
        >
          {children}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  )
}

export const Popover = {
  Root: PopoverRoot,
  Trigger: PopoverTrigger,
  Content: PopoverContent,
}
